import { post, patch, get, del } from 'aws-amplify/api';
import { processResponse } from './api_util';
import useSWR from 'swr'
// import { handleApiResponse } from './api_util';



export async function sendChatMessage(message) {
  const payload = {
    apiName: 'LivisiblyChatApi',
    path: `chat`,
    options: {
      body: {
        message: message,
      }
    }
  }

  const restOperation = post(payload);


  const response = await restOperation.response;
  console.log("sendChatMessage response:",response)
  return processResponse(response)
}

export function useSendChatMessage(message, shouldFetch = true) {
  const result = useSWR(
    shouldFetch ? 'CHAT' : null,
    (path) => sendChatMessage(message)
  );
  return result;

}

// The browser's IANA time zone (e.g. "America/New_York"). It rides on every chat message; the backend keeps it on
// the session so Livi gives dates and times in the user's zone instead of UTC.
export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (e) {
    return undefined;
  }
}

export async function sendChatMessageV2(messageData) {
  const clientTimeZone = browserTimeZone();
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: `chat`,
    options: {
      body: { ...messageData, ...(clientTimeZone ? { clientTimeZone } : {}) }
    }
  }

  const restOperation = post(payload);


  const response = await restOperation.response;
  console.log("sendChatMessage response:",response)
  return processResponse(response)
}

export async function applyScanCorrections(correctionData) {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/corrections',
    options: {
      body: correctionData
    }
  };
  const restOperation = post(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

/**
 * A thumbs, and an optional reason, on one of Livi's replies:
 * { sessionID, message_id, timestamp, rating: 'up' | 'down', reason? }.
 */
export async function sendReplyFeedback(feedback) {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/feedback',
    options: {
      body: feedback
    }
  };
  const restOperation = post(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

/**
 * Composite session-open fetch — returns messages + state + meta + persisted
 * center panel + chat list in ONE backend call. Frontend uses this on
 * session-change to avoid the parallel fan-out that previously hit 3-4
 * separate Lambda containers (each paying its own ~5-8s auth/context/
 * bucket-role tax per CloudWatch 2026-05-07).
 *
 * If sessionID is omitted, only the chat list is returned (used for the
 * dropdown when no session is yet selected).
 */
export async function getChatSessionContext(sessionID, { includeChatList = true, sessionType } = {}) {
  const queryParams = { include_chat_list: includeChatList ? 'true' : 'false' };
  if (sessionID) queryParams.sessionID = sessionID;
  // Optional session_type hint — needed for SYSTEM_WORK sessions which
  // live in the global system DDB table, not per-case-hil. Without the
  // hint the backend defaults to case-DDB and the SYSTEM_WORK session
  // appears empty. Passed through identically to /chat/messages.
  if (sessionType) queryParams.session_type = sessionType;
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/session_context',
    options: { queryParams },
  };
  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

/**
 * Find or create the dedicated chat session for a topic. Each topic gets one
 * session so the agent's reasoning stays scoped to a single hypothesis. The
 * dashboard Topics panel calls this on row click and then opens the returned
 * sessionID via openChatSession — keeps internal IDs out of chat history and
 * prevents topic discussions from polluting unrelated sessions.
 *
 * Returns { sessionID, did_create, topicTitle }.
 */
export async function findOrCreateTopicSession(topicID) {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/topic-session',
    options: {
      body: { topicID },
    },
  };
  const restOperation = post(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

/**
 * Open a new super-admin SYSTEM_WORK chat for reviewing Livi lessons (Learning
 * Loop 1.3). With `lessonProposalId`, Livi opens by summarizing that proposal
 * with a recommendation. Open the returned session with
 * openChatSession(sessionID, { sessionType: 'SYSTEM_WORK' }).
 *
 * Returns { sessionID, sessionType }.
 */
export async function createLessonReviewSession(lessonProposalId) {
  const body = { purpose: 'lesson_review' };
  if (lessonProposalId) body.lesson_proposal_id = lessonProposalId;
  const restOperation = post({
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/system-session',
    options: { body },
  });
  const response = await restOperation.response;
  return processResponse(response);
}

/**
 * Find or create the caller's CASE_INTERVIEW session. Each contributor gets
 * their own private interview thread per case (one session per (case, user)).
 * The dashboard "Conduct case interview" attention card calls this on click
 * and then opens the returned sessionID via openChatSession.
 *
 * No body — backend uses the caller's cognitoUserID. Cross-user reads land
 * post-v1 alongside the trust hierarchy work.
 *
 * Returns { sessionID, did_create, ownerCognitoUserID }.
 */
export async function findOrCreateInterviewSession() {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/interview-session',
    options: { body: {} },
  };
  const restOperation = post(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

/**
 * Route the current reviewer to the HIL scan review they should work on, and
 * flip it PENDING -> ACTIVE server-side if nobody had started it yet.
 *
 * Must be a POST rather than re-reading /case_stats: the dashboard tile used to
 * re-resolve the same oldest-pending session id on every click, so every
 * reviewer landed on the SAME unstarted scan and silently duplicated work.
 *
 * Routing is: a still-open session you have already worked on, else the oldest
 * PENDING one (promoted to ACTIVE), else the oldest ACTIVE one. This is NOT an
 * assignment — there is no assignee and no lock, and two reviewers may still
 * legitimately share an ACTIVE session.
 *
 * Returns { sessionID, rule, did_promote, open_count }; sessionID is null when
 * the case has no open HIL reviews left.
 */
export async function routeToNextHilSession() {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/hil/next',
    options: { body: {} },
  };
  const restOperation = post(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

/**
 * Fetch the persisted center-panel render for a session so the frontend can
 * hydrate immediately on mount — avoiding the AppSync subscription race where
 * the first render event can be lost if the subscription is still connecting.
 * Returns { payload: <renderPayload> | null }.
 */
export async function getCenterPanel(sessionID) {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/center_panel',
    options: {
      queryParams: { sessionID },
    },
  };
  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

/**
 * GET /chat/topic_detail — the CURRENT topic-detail render payload, built live
 * from Neo4j (NOT the persisted per-session snapshot). Returns
 * { payload: <renderPayload> | null }.
 */
export async function getTopicDetail(topicID) {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/topic_detail',
    options: {
      queryParams: { topicID },
    },
  };
  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

/**
 * Live topic-detail payload, polled so an already-rendered TopicDetailPanel
 * reflects evidence/title changes made AFTER the agent first rendered it — e.g.
 * a headless DISCOVERY_DIG that attaches transactions in a different session
 * and so can't re-publish this user's center-panel snapshot. Mirrors
 * useGetCaseStats' 7s refresh; the dashboard card is already live this way, so
 * this brings the chat panel to parity.
 */
export function useTopicDetail(topicID, shouldFetch = true) {
  const result = useSWR(
    shouldFetch && topicID ? ['TOPIC_DETAIL', topicID] : null,
    () => getTopicDetail(topicID),
    { refreshInterval: 7000 }
  );
  return result;
}

// export function useSendChatMessageV2(messageData, shouldFetch = true) {
//   const result = useSWR(
//     shouldFetch ? 'CHAT' : null,
//     (path) => sendChatMessageV2(messageData)
//   );
//   return result;

// }



async function getChatHistory(parameters) {
  console.log(`topic getChatHistory parameters:`, parameters);
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: `chat/messages`,
    options: {
      queryParams: parameters
    }
  }

  const restOperation = get(payload);


  const response = await restOperation.response;

  return processResponse(response)
}

export function useGetChatHistory(parameters, shouldFetch = true) {
  //console.log('topic useGetChatHistory parameters:', parameters);
  const result = useSWR(
    shouldFetch ? ['CHAT_HISTORY', parameters] : null,
    ([key, params]) => getChatHistory(params),
    {
      refreshInterval: 0, // changed after adding appsync, was 2000 earlier for polling
      revalidateOnFocus: true,
      dedupingInterval: 1000
    }
  );
  return result;
}

/**
 * Get list of all chats for the current case
 */
async function getChatList() {
  console.log('getChatList called');

  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/list',
    options: {}
  };

  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

export function useGetChatList(shouldFetch = true) {
  const result = useSWR(
    shouldFetch ? 'CHAT_LIST' : null,
    getChatList,
    {
      // 30s, not 10s: this is a case-wide list whose cost scales with the
      // number of sessions. At 10s against a slow list the client kept
      // several overlapping requests in flight, each doing thousands of
      // DynamoDB reads (Trello YMElu5nK). Kept tight enough for HIL-queue
      // freshness — revalidateOnFocus still gives an immediate refresh when
      // the user comes back to the tab.
      refreshInterval: 30000,
      revalidateOnFocus: true,
      dedupingInterval: 5000
    }
  );

  return result;
}


// ---------------------------------------------------------------------------
// Punch List API
// ---------------------------------------------------------------------------

async function getPunchListItems(parameters) {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/punch_list',
    options: { queryParams: parameters }
  };
  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

export function useGetPunchListItems(parameters, shouldFetch = true) {
  const result = useSWR(
    shouldFetch ? ['PUNCH_LIST', parameters] : null,
    ([key, params]) => getPunchListItems(params),
    {
      refreshInterval: 0,
      revalidateOnFocus: true,
      dedupingInterval: 1000
    }
  );
  return result;
}

export async function addPunchListItem(itemData) {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/punch_list',
    options: { body: itemData }
  };
  const restOperation = post(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

export async function updatePunchListItem(itemData) {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/punch_list',
    options: { body: itemData }
  };
  const restOperation = patch(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

export async function resolvePunchListItem(itemData) {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/punch_list/resolve',
    options: { body: itemData }
  };
  const restOperation = post(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

export async function deletePunchListItem(itemData) {
  const payload = {
    apiName: 'LivisiblyChatMessageApi',
    path: 'chat/punch_list',
    options: { body: itemData }
  };
  const restOperation = del(payload);
  const response = await restOperation.response;
  return processResponse(response);
}